// src/pages/UserAnalyticsPage.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFirestore, collection, getDocs } from 'firebase/firestore';
import { auth } from '../firebaseConfig';
import { API_BASE_URL } from '../services/api';
import { useRegion } from '../context/RegionContext';
import './UserAnalyticsPage.css';

/* ────────────────────────────────────────────
   Helper functions
   ──────────────────────────────────────────── */

const timeAgo = (dateStr) => {
  if (!dateStr) return 'Never';
  const diff = (new Date() - new Date(dateStr)) / 1000;
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString();
};

const getInitials = (email) => {
  if (!email) return '?';
  return email.split('@')[0].substring(0, 2).toUpperCase();
};

const isOnline = (lastLogin) => {
  if (!lastLogin) return false;
  return (new Date() - new Date(lastLogin)) / (1000 * 60 * 60) < 1;
};

const isActiveIn24h = (lastLogin) => {
  if (!lastLogin) return false;
  return (new Date() - new Date(lastLogin)) / (1000 * 60 * 60) < 24;
};

/** Format total seconds into a human-readable string */
const formatDuration = (totalSeconds) => {
  if (!totalSeconds || totalSeconds <= 0) return '0m';
  const hrs = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  if (hrs === 0) return `${mins}m`;
  if (mins === 0) return `${hrs}h`;
  return `${hrs}h ${mins}m`;
};

const regionNameMap = {
  'vquest': 'New Zealand',
  'vquest-au-1': 'Australia',
  'vquest-uk-1': 'United Kingdom',
  'vquest-uk-ICE': 'UK - ICE',
  'vquest-us-1': 'USA/Canada',
  'vquest-irl-1': 'Ireland',
  'vquest-midrise': 'Mid Rise',
};

/* ────────────────────────────────────────────
   Stat Card Component
   ──────────────────────────────────────────── */
const StatCard = ({ icon, label, value, subtitle, color = 'teal' }) => (
  <div className={`ua-stat-card ua-stat-card--${color}`}>
    <div className="ua-stat-card__icon">{icon}</div>
    <div className="ua-stat-card__content">
      <div className="ua-stat-card__value">{value}</div>
      <div className="ua-stat-card__label">{label}</div>
      {subtitle && <div className="ua-stat-card__subtitle">{subtitle}</div>}
    </div>
  </div>
);

/* ────────────────────────────────────────────
   Mini Bar Chart (pure CSS)
   ──────────────────────────────────────────── */
const MiniBarChart = ({ data, maxValue, label }) => (
  <div className="ua-mini-chart">
    <div className="ua-mini-chart__bars">
      {data.map((item, i) => (
        <div key={i} className="ua-mini-chart__bar-wrapper" title={`${item.label}: ${item.value}`}>
          <div
            className="ua-mini-chart__bar"
            style={{ height: `${maxValue > 0 ? (item.value / maxValue) * 100 : 0}%` }}
          />
          <span className="ua-mini-chart__bar-label">{item.shortLabel}</span>
        </div>
      ))}
    </div>
    {label && <div className="ua-mini-chart__label">{label}</div>}
  </div>
);

/* ════════════════════════════════════════════
   Main Component
   ════════════════════════════════════════════ */

function UserAnalyticsPage() {
  const navigate = useNavigate();
  const db = getFirestore();
  const { selectedRegion } = useRegion();

  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [materialRequests, setMaterialRequests] = useState([]);
  const [compositeRequests, setCompositeRequests] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [sortField, setSortField] = useState('lastLogin');
  const [sortDir, setSortDir] = useState('desc');
  const [searchQuery, setSearchQuery] = useState('');

  // Session time tab state
  const [sessionSortField, setSessionSortField] = useState('totalSessionSeconds');
  const [sessionSortDir, setSessionSortDir] = useState('desc');
  const [sessionSearchQuery, setSessionSearchQuery] = useState('');
  const [sessionRoleFilter, setSessionRoleFilter] = useState('all');

  /* ── Fetch all data ── */
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Fetch users from Firestore
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersData = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersData);

      // 2. Fetch material submissions
      try {
        const token = await auth.currentUser?.getIdToken();
        if (token) {
          const headers = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'x-region': selectedRegion || 'vquest',
            'x-user-role': 'admin',
            'x-user-email': auth.currentUser?.email || '',
            'x-user-id': auth.currentUser?.uid || '',
          };

          const [matRes, compRes] = await Promise.all([
            fetch(`${API_BASE_URL}/material-requests/all`, { headers }).then(r => r.ok ? r.json() : { requests: [] }),
            fetch(`${API_BASE_URL}/composite-requests/pending`, { headers }).then(r => r.ok ? r.json() : []),
          ]);

          setMaterialRequests(matRes.requests || matRes || []);
          setCompositeRequests(Array.isArray(compRes) ? compRes : []);
        }
      } catch (apiErr) {
        console.error('⚠️ Error fetching request data:', apiErr);
      }

    } catch (err) {
      console.error('❌ Error loading analytics data:', err);
    } finally {
      setLoading(false);
    }
  }, [db, selectedRegion]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  /* ── Derived analytics ── */

  const analytics = useMemo(() => {
    const now = new Date();
    const today = new Date().toISOString().split('T')[0];

    // Status counts
    const statusCounts = { active: 0, pending: 0, suspended: 0, rejected: 0 };
    const roleCounts = { admin: 0, supplier: 0, user: 0 };
    const onlineNow = [];
    const activeIn24h = [];
    const activeIn7d = [];
    const neverLoggedIn = [];

    // Login activity by day of week
    const dayOfWeekActivity = [0, 0, 0, 0, 0, 0, 0]; // Sun-Sat
    const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    // Login activity by hour
    const hourActivity = new Array(24).fill(0);

    // Monthly signups
    const monthlySignups = {};

    // Region distribution
    const regionDist = {};

    // Session time tracking
    let totalAllTimeSeconds = 0;
    let totalTodaySeconds = 0;

    users.forEach(user => {
      // Status
      const status = user.status || 'unknown';
      if (statusCounts[status] !== undefined) statusCounts[status]++;

      // Role
      const role = user.role || 'user';
      if (roleCounts[role] !== undefined) roleCounts[role]++;
      else roleCounts[role] = 1;

      // Online status
      if (isOnline(user.lastLogin)) onlineNow.push(user);
      if (isActiveIn24h(user.lastLogin)) activeIn24h.push(user);

      // Active in 7 days
      if (user.lastLogin) {
        const diff = (now - new Date(user.lastLogin)) / (1000 * 60 * 60);
        if (diff < 168) activeIn7d.push(user);

        // Day of week
        const day = new Date(user.lastLogin).getDay();
        dayOfWeekActivity[day]++;

        // Hour
        const hour = new Date(user.lastLogin).getHours();
        hourActivity[hour]++;
      } else {
        neverLoggedIn.push(user);
      }

      // Monthly signups
      if (user.createdAt) {
        const month = new Date(user.createdAt).toLocaleString('default', { month: 'short', year: 'numeric' });
        monthlySignups[month] = (monthlySignups[month] || 0) + 1;
      }

      // Regions
      if (user.allowedRegions) {
        user.allowedRegions.forEach(r => {
          const name = regionNameMap[r] || r;
          regionDist[name] = (regionDist[name] || 0) + 1;
        });
      }

      // Session seconds
      totalAllTimeSeconds += (user.totalSessionSeconds || 0);
      if (user.sessionDate === today) {
        totalTodaySeconds += (user.todaySessionSeconds || 0);
      }
    });

    // Request stats by user
    const requestsByUser = {};
    materialRequests.forEach(req => {
      const email = req.SubmittedByEmail || 'unknown';
      if (!requestsByUser[email]) requestsByUser[email] = { material: 0, composite: 0 };
      requestsByUser[email].material++;
    });
    compositeRequests.forEach(req => {
      const email = req.SubmittedByEmail || 'unknown';
      if (!requestsByUser[email]) requestsByUser[email] = { material: 0, composite: 0 };
      requestsByUser[email].composite++;
    });

    // Top contributors
    const topContributors = Object.entries(requestsByUser)
      .map(([email, counts]) => ({ email, ...counts, total: counts.material + counts.composite }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);

    // Day of week chart data
    const maxDayActivity = Math.max(...dayOfWeekActivity, 1);
    const dayChartData = dayOfWeekActivity.map((val, i) => ({
      label: dayLabels[i],
      shortLabel: dayLabels[i],
      value: val,
    }));

    // Hour chart data (group into 4-hour blocks)
    const hourBlocks = [];
    for (let i = 0; i < 24; i += 2) {
      hourBlocks.push({
        label: `${i}:00-${i + 1}:59`,
        shortLabel: `${i}`,
        value: hourActivity[i] + hourActivity[i + 1],
      });
    }
    const maxHourActivity = Math.max(...hourBlocks.map(b => b.value), 1);

    // Monthly signup chart
    const monthEntries = Object.entries(monthlySignups).slice(-12);
    const maxMonthly = Math.max(...monthEntries.map(([, v]) => v), 1);
    const monthChartData = monthEntries.map(([label, value]) => ({
      label,
      shortLabel: label.split(' ')[0],
      value,
    }));

    // Session analytics
    const usersWithSessions = users
      .filter(u => (u.totalSessionSeconds || 0) > 0)
      .sort((a, b) => (b.totalSessionSeconds || 0) - (a.totalSessionSeconds || 0));

    const avgSessionSeconds = usersWithSessions.length > 0
      ? Math.round(totalAllTimeSeconds / usersWithSessions.length)
      : 0;

    const sessionByRole = {};
    users.forEach(u => {
      const role = u.role || 'user';
      if (!sessionByRole[role]) sessionByRole[role] = { total: 0, count: 0 };
      sessionByRole[role].total += (u.totalSessionSeconds || 0);
      if ((u.totalSessionSeconds || 0) > 0) sessionByRole[role].count++;
    });

    return {
      totalUsers: users.length,
      statusCounts,
      roleCounts,
      onlineNow,
      activeIn24h,
      activeIn7d,
      neverLoggedIn,
      dayChartData,
      maxDayActivity,
      hourBlocks,
      maxHourActivity,
      monthChartData,
      maxMonthly,
      regionDist,
      topContributors,
      totalMaterialRequests: materialRequests.length,
      totalCompositeRequests: compositeRequests.length,
      requestsByUser,
      // Session analytics
      totalAllTimeSeconds,
      totalTodaySeconds,
      avgSessionSeconds,
      usersWithSessions,
      sessionByRole,
    };
  }, [users, materialRequests, compositeRequests]);

  /* ── Sorted & filtered user list ── */
  const sortedUsers = useMemo(() => {
    let filtered = [...users];

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(u =>
        (u.email || '').toLowerCase().includes(q) ||
        (u.role || '').toLowerCase().includes(q) ||
        (u.status || '').toLowerCase().includes(q) ||
        (u.companyName || '').toLowerCase().includes(q)
      );
    }

    filtered.sort((a, b) => {
      let aVal = a[sortField] || '';
      let bVal = b[sortField] || '';
      if (sortField === 'lastLogin' || sortField === 'createdAt') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [users, searchQuery, sortField, sortDir]);

  /* ── Sorted & filtered session users ── */
  const sortedSessionUsers = useMemo(() => {
    let filtered = [...users];

    if (sessionSearchQuery) {
      const q = sessionSearchQuery.toLowerCase();
      filtered = filtered.filter(u =>
        (u.email || '').toLowerCase().includes(q) ||
        (u.role || '').toLowerCase().includes(q) ||
        (u.companyName || '').toLowerCase().includes(q)
      );
    }

    if (sessionRoleFilter !== 'all') {
      filtered = filtered.filter(u => (u.role || 'user') === sessionRoleFilter);
    }

    filtered.sort((a, b) => {
      let aVal, bVal;
      if (sessionSortField === 'totalSessionSeconds' || sessionSortField === 'todaySessionSeconds') {
        aVal = a[sessionSortField] || 0;
        bVal = b[sessionSortField] || 0;
      } else if (sessionSortField === 'lastLogin') {
        aVal = a.lastLogin ? new Date(a.lastLogin).getTime() : 0;
        bVal = b.lastLogin ? new Date(b.lastLogin).getTime() : 0;
      } else {
        aVal = (a[sessionSortField] || '').toString().toLowerCase();
        bVal = (b[sessionSortField] || '').toString().toLowerCase();
      }
      if (aVal < bVal) return sessionSortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sessionSortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [users, sessionSearchQuery, sessionRoleFilter, sessionSortField, sessionSortDir]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const handleSessionSort = (field) => {
    if (sessionSortField === field) {
      setSessionSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSessionSortField(field);
      setSessionSortDir('desc');
    }
  };

  const sortIcon = (field) => {
    if (sortField !== field) return ' ↕';
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  };

  const sessionSortIcon = (field) => {
    if (sessionSortField !== field) return ' ↕';
    return sessionSortDir === 'asc' ? ' ↑' : ' ↓';
  };

  /* ── Render helpers ── */

  const renderOverview = () => (
    <div className="ua-overview">
      {/* Stat cards row */}
      <div className="ua-stat-row">
        <StatCard icon="👥" label="Total Users" value={analytics.totalUsers} color="teal" />
        <StatCard icon="🟢" label="Online Now" value={analytics.onlineNow.length} subtitle="Last 1 hour" color="emerald" />
        <StatCard icon="⚡" label="Active (24h)" value={analytics.activeIn24h.length} color="sky" />
        <StatCard icon="📅" label="Active (7d)" value={analytics.activeIn7d.length} color="indigo" />
        <StatCard icon="😴" label="Never Logged In" value={analytics.neverLoggedIn.length} color="amber" />
      </div>

      {/* Charts row */}
      <div className="ua-charts-row">
        <div className="ua-panel">
          <h3 className="ua-panel__title">📊 Login Activity by Day</h3>
          <MiniBarChart data={analytics.dayChartData} maxValue={analytics.maxDayActivity} label="Day of week (last login)" />
        </div>

        <div className="ua-panel">
          <h3 className="ua-panel__title">🕐 Login Activity by Hour</h3>
          <MiniBarChart data={analytics.hourBlocks} maxValue={analytics.maxHourActivity} label="Hour of day (2h blocks)" />
        </div>

        <div className="ua-panel">
          <h3 className="ua-panel__title">📈 User Signups</h3>
          <MiniBarChart data={analytics.monthChartData} maxValue={analytics.maxMonthly} label="Monthly signups" />
        </div>
      </div>

      {/* Distribution row */}
      <div className="ua-charts-row">
        <div className="ua-panel">
          <h3 className="ua-panel__title">🎭 Users by Role</h3>
          <div className="ua-distribution">
            {Object.entries(analytics.roleCounts).map(([role, count]) => (
              <div key={role} className="ua-dist-item">
                <div className="ua-dist-item__header">
                  <span className={`ua-role-dot ua-role-dot--${role}`} />
                  <span className="ua-dist-item__label">{role}</span>
                  <span className="ua-dist-item__count">{count}</span>
                </div>
                <div className="ua-dist-item__bar-bg">
                  <div
                    className={`ua-dist-item__bar ua-dist-item__bar--${role}`}
                    style={{ width: `${analytics.totalUsers > 0 ? (count / analytics.totalUsers) * 100 : 0}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="ua-panel">
          <h3 className="ua-panel__title">📋 Users by Status</h3>
          <div className="ua-distribution">
            {Object.entries(analytics.statusCounts).map(([status, count]) => (
              <div key={status} className="ua-dist-item">
                <div className="ua-dist-item__header">
                  <span className={`ua-status-dot ua-status-dot--${status}`} />
                  <span className="ua-dist-item__label">{status}</span>
                  <span className="ua-dist-item__count">{count}</span>
                </div>
                <div className="ua-dist-item__bar-bg">
                  <div
                    className={`ua-dist-item__bar ua-dist-item__bar--${status}`}
                    style={{ width: `${analytics.totalUsers > 0 ? (count / analytics.totalUsers) * 100 : 0}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="ua-panel">
          <h3 className="ua-panel__title">🌏 Users by Region</h3>
          <div className="ua-distribution">
            {Object.entries(analytics.regionDist)
              .sort(([, a], [, b]) => b - a)
              .map(([region, count]) => (
                <div key={region} className="ua-dist-item">
                  <div className="ua-dist-item__header">
                    <span className="ua-dist-item__label">{region}</span>
                    <span className="ua-dist-item__count">{count}</span>
                  </div>
                  <div className="ua-dist-item__bar-bg">
                    <div
                      className="ua-dist-item__bar ua-dist-item__bar--region"
                      style={{ width: `${analytics.totalUsers > 0 ? (count / analytics.totalUsers) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              ))}
            {Object.keys(analytics.regionDist).length === 0 && (
              <p className="ua-empty-text">No region data available</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderRequests = () => (
    <div className="ua-requests">
      <div className="ua-stat-row">
        <StatCard icon="📦" label="Product Submissions" value={analytics.totalMaterialRequests} color="rose" />
        <StatCard icon="🏗️" label="Composite Requests" value={analytics.totalCompositeRequests} color="slate" />
        <StatCard
          icon="🏆"
          label="Top Contributor"
          value={analytics.topContributors[0]?.email?.split('@')[0] || 'N/A'}
          subtitle={analytics.topContributors[0] ? `${analytics.topContributors[0].total} requests` : ''}
          color="amber"
        />
      </div>

      <div className="ua-panel ua-panel--wide">
        <h3 className="ua-panel__title">🏆 Top Contributors</h3>
        {analytics.topContributors.length > 0 ? (
          <table className="ua-table">
            <thead>
              <tr>
                <th>#</th>
                <th>User</th>
                <th>Product Submissions</th>
                <th>Composite Requests</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {analytics.topContributors.map((user, i) => (
                <tr key={user.email}>
                  <td>
                    <span className="ua-rank">{i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : i + 1}</span>
                  </td>
                  <td>
                    <div className="ua-user-cell">
                      <div className="ua-avatar ua-avatar--small">{getInitials(user.email)}</div>
                      <span>{user.email}</span>
                    </div>
                  </td>
                  <td>{user.material}</td>
                  <td>{user.composite}</td>
                  <td><strong>{user.total}</strong></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="ua-empty-text">No request data available</p>
        )}
      </div>
    </div>
  );

  const renderUserList = () => (
    <div className="ua-user-list">
      <div className="ua-search-bar">
        <input
          type="text"
          placeholder="Search by email, role, status, or company..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="ua-search-input"
        />
        <span className="ua-search-count">{sortedUsers.length} users</span>
      </div>

      <div className="ua-table-wrapper">
        <table className="ua-table ua-table--full">
          <thead>
            <tr>
              <th>User</th>
              <th className="ua-sortable" onClick={() => handleSort('role')}>
                Role{sortIcon('role')}
              </th>
              <th className="ua-sortable" onClick={() => handleSort('status')}>
                Status{sortIcon('status')}
              </th>
              <th className="ua-sortable" onClick={() => handleSort('lastLogin')}>
                Last Login{sortIcon('lastLogin')}
              </th>
              <th className="ua-sortable" onClick={() => handleSort('createdAt')}>
                Joined{sortIcon('createdAt')}
              </th>
              <th>Regions</th>
              <th>Activity</th>
            </tr>
          </thead>
          <tbody>
            {sortedUsers.map(user => {
              const userReqs = analytics.requestsByUser[user.email] || { material: 0, composite: 0 };
              const online = isOnline(user.lastLogin);

              return (
                <tr key={user.id} className={online ? 'ua-row--online' : ''}>
                  <td>
                    <div className="ua-user-cell">
                      <div className={`ua-avatar ${online ? 'ua-avatar--online' : ''}`}>
                        {getInitials(user.email)}
                      </div>
                      <div>
                        <div className="ua-user-email">{user.email}</div>
                        {user.companyName && (
                          <div className="ua-user-company">{user.companyName}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td><span className={`ua-badge ua-badge--${user.role || 'user'}`}>{user.role || 'user'}</span></td>
                  <td><span className={`ua-badge ua-badge--${user.status || 'unknown'}`}>{user.status || 'unknown'}</span></td>
                  <td>
                    <div className="ua-login-info">
                      <span className={`ua-online-dot ${online ? 'ua-online-dot--on' : ''}`} />
                      {timeAgo(user.lastLogin)}
                    </div>
                  </td>
                  <td>{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}</td>
                  <td>
                    <div className="ua-region-tags">
                      {user.allowedRegions?.map(r => (
                        <span key={r} className="ua-region-tag">{regionNameMap[r] || r}</span>
                      )) || <span className="ua-empty-text">—</span>}
                    </div>
                  </td>
                  <td>
                    <div className="ua-activity-badges">
                      {userReqs.material > 0 && (
                        <span className="ua-activity-badge ua-activity-badge--material" title="Product submissions">
                          📦 {userReqs.material}
                        </span>
                      )}
                      {userReqs.composite > 0 && (
                        <span className="ua-activity-badge ua-activity-badge--composite" title="Composite requests">
                          🏗️ {userReqs.composite}
                        </span>
                      )}
                      {userReqs.material === 0 && userReqs.composite === 0 && (
                        <span className="ua-empty-text">No requests</span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderOnlineNow = () => (
    <div className="ua-online-section">
      <div className="ua-stat-row">
        <StatCard icon="🟢" label="Online Now" value={analytics.onlineNow.length} subtitle="Active in last hour" color="emerald" />
        <StatCard icon="⚡" label="Active Today" value={analytics.activeIn24h.length} subtitle="Active in last 24 hours" color="sky" />
      </div>

      <div className="ua-panel ua-panel--wide">
        <h3 className="ua-panel__title">🟢 Currently Online Users</h3>
        {analytics.onlineNow.length > 0 ? (
          <div className="ua-online-grid">
            {analytics.onlineNow.map(user => (
              <div key={user.id} className="ua-online-card">
                <div className="ua-avatar ua-avatar--online ua-avatar--large">
                  {getInitials(user.email)}
                </div>
                <div className="ua-online-card__info">
                  <div className="ua-online-card__email">{user.email}</div>
                  <div className="ua-online-card__meta">
                    <span className={`ua-badge ua-badge--${user.role}`}>{user.role}</span>
                    <span className="ua-online-card__time">{timeAgo(user.lastLogin)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="ua-empty-state">
            <div className="ua-empty-state__icon">💤</div>
            <p>No users currently online</p>
          </div>
        )}
      </div>

      <div className="ua-panel ua-panel--wide">
        <h3 className="ua-panel__title">⚡ Active in Last 24 Hours</h3>
        {analytics.activeIn24h.length > 0 ? (
          <div className="ua-online-grid">
            {analytics.activeIn24h
              .filter(u => !isOnline(u.lastLogin))
              .map(user => (
                <div key={user.id} className="ua-online-card ua-online-card--away">
                  <div className="ua-avatar ua-avatar--large">
                    {getInitials(user.email)}
                  </div>
                  <div className="ua-online-card__info">
                    <div className="ua-online-card__email">{user.email}</div>
                    <div className="ua-online-card__meta">
                      <span className={`ua-badge ua-badge--${user.role}`}>{user.role}</span>
                      <span className="ua-online-card__time">{timeAgo(user.lastLogin)}</span>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        ) : (
          <div className="ua-empty-state">
            <div className="ua-empty-state__icon">📭</div>
            <p>No additional users active in the last 24 hours</p>
          </div>
        )}
      </div>
    </div>
  );

  /* ── Session Time Tab ── */
  const renderSessionTime = () => {
    const today = new Date().toISOString().split('T')[0];
    const topUser = analytics.usersWithSessions[0];
    const maxSeconds = topUser?.totalSessionSeconds || 1;

    return (
      <div className="ua-session-section">
        {/* Stat cards */}
        <div className="ua-stat-row">
          <StatCard
            icon="⏱️"
            label="Total Tracked Time"
            value={formatDuration(analytics.totalAllTimeSeconds)}
            subtitle="All users combined"
            color="teal"
          />
          <StatCard
            icon="📅"
            label="Today's Usage"
            value={formatDuration(analytics.totalTodaySeconds)}
            subtitle="All users today"
            color="sky"
          />
          <StatCard
            icon="📊"
            label="Avg Per User"
            value={formatDuration(analytics.avgSessionSeconds)}
            subtitle={`${analytics.usersWithSessions.length} tracked users`}
            color="indigo"
          />
          <StatCard
            icon="🏆"
            label="Most Active"
            value={topUser?.email?.split('@')[0] || 'N/A'}
            subtitle={topUser ? formatDuration(topUser.totalSessionSeconds) : ''}
            color="amber"
          />
        </div>

        {/* Usage by role + leaderboard */}
        <div className="ua-charts-row">
          <div className="ua-panel">
            <h3 className="ua-panel__title">🎭 Usage by Role</h3>
            <div className="ua-distribution">
              {Object.entries(analytics.sessionByRole)
                .sort(([, a], [, b]) => b.total - a.total)
                .map(([role, data]) => (
                  <div key={role} className="ua-dist-item">
                    <div className="ua-dist-item__header">
                      <span className={`ua-role-dot ua-role-dot--${role}`} />
                      <span className="ua-dist-item__label">{role}</span>
                      <span className="ua-dist-item__count">{formatDuration(data.total)}</span>
                    </div>
                    <div className="ua-dist-item__bar-bg">
                      <div
                        className={`ua-dist-item__bar ua-dist-item__bar--${role}`}
                        style={{ width: `${analytics.totalAllTimeSeconds > 0 ? (data.total / analytics.totalAllTimeSeconds) * 100 : 0}%` }}
                      />
                    </div>
                    <div className="ua-dist-item__sub">
                      {data.count} user{data.count !== 1 ? 's' : ''} · avg {formatDuration(data.count > 0 ? Math.round(data.total / data.count) : 0)}
                    </div>
                  </div>
                ))}
            </div>
          </div>

          <div className="ua-panel ua-panel--wide">
            <h3 className="ua-panel__title">🏆 Session Time Leaderboard</h3>
            {analytics.usersWithSessions.length > 0 ? (
              <div className="ua-leaderboard">
                {analytics.usersWithSessions.slice(0, 5).map((user, i) => {
                  const pct = maxSeconds > 0 ? ((user.totalSessionSeconds || 0) / maxSeconds) * 100 : 0;
                  const todaySecs = user.sessionDate === today ? (user.todaySessionSeconds || 0) : 0;
                  const online = isOnline(user.lastLogin);

                  return (
                    <div key={user.id} className="ua-leaderboard__item">
                      <span className="ua-leaderboard__rank">
                        {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
                      </span>
                      <div className={`ua-avatar ${online ? 'ua-avatar--online' : ''}`}>
                        {getInitials(user.email)}
                      </div>
                      <div className="ua-leaderboard__info">
                        <div className="ua-leaderboard__email">{user.email}</div>
                        <div className="ua-leaderboard__bar-bg">
                          <div className="ua-leaderboard__bar" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                      <div className="ua-leaderboard__stats">
                        <div className="ua-leaderboard__total">{formatDuration(user.totalSessionSeconds)}</div>
                        {todaySecs > 0 && (
                          <div className="ua-leaderboard__today">Today: {formatDuration(todaySecs)}</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="ua-empty-state">
                <div className="ua-empty-state__icon">⏱️</div>
                <p>No session data tracked yet</p>
                <p className="ua-empty-text">Session tracking starts once users interact with the app</p>
              </div>
            )}
          </div>
        </div>

        {/* Full user table */}
        <div className="ua-panel ua-panel--wide">
          <h3 className="ua-panel__title">📋 All User Sessions</h3>

          <div className="ua-search-bar">
            <input
              type="text"
              placeholder="Search by email or company..."
              value={sessionSearchQuery}
              onChange={(e) => setSessionSearchQuery(e.target.value)}
              className="ua-search-input"
            />
            <select
              className="ua-role-filter"
              value={sessionRoleFilter}
              onChange={(e) => setSessionRoleFilter(e.target.value)}
            >
              <option value="all">All Roles</option>
              <option value="admin">Admin</option>
              <option value="supplier">Supplier</option>
              <option value="user">User</option>
            </select>
            <span className="ua-search-count">{sortedSessionUsers.length} users</span>
          </div>

          <div className="ua-table-wrapper">
            <table className="ua-table ua-table--full">
              <thead>
                <tr>
                  <th>#</th>
                  <th>User</th>
                  <th className="ua-sortable" onClick={() => handleSessionSort('role')}>
                    Role{sessionSortIcon('role')}
                  </th>
                  <th className="ua-sortable" onClick={() => handleSessionSort('totalSessionSeconds')}>
                    All-Time{sessionSortIcon('totalSessionSeconds')}
                  </th>
                  <th className="ua-sortable" onClick={() => handleSessionSort('todaySessionSeconds')}>
                    Today{sessionSortIcon('todaySessionSeconds')}
                  </th>
                  <th className="ua-sortable" onClick={() => handleSessionSort('lastLogin')}>
                    Last Active{sessionSortIcon('lastLogin')}
                  </th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {sortedSessionUsers.map((user, i) => {
                  const totalSecs = user.totalSessionSeconds || 0;
                  const todaySecs = user.sessionDate === today ? (user.todaySessionSeconds || 0) : 0;
                  const online = isOnline(user.lastLogin);
                  const pct = maxSeconds > 0 ? (totalSecs / maxSeconds) * 100 : 0;

                  return (
                    <tr key={user.id} className={online ? 'ua-row--online' : ''}>
                      <td>
                        <span className="ua-rank">
                          {i === 0 && sessionSortField === 'totalSessionSeconds' && sessionSortDir === 'desc'
                            ? '🥇' : i === 1 && sessionSortField === 'totalSessionSeconds' && sessionSortDir === 'desc'
                            ? '🥈' : i === 2 && sessionSortField === 'totalSessionSeconds' && sessionSortDir === 'desc'
                            ? '🥉' : i + 1}
                        </span>
                      </td>
                      <td>
                        <div className="ua-user-cell">
                          <div className={`ua-avatar ${online ? 'ua-avatar--online' : ''}`}>
                            {getInitials(user.email)}
                          </div>
                          <div>
                            <div className="ua-user-email">{user.email}</div>
                            {user.companyName && (
                              <div className="ua-user-company">{user.companyName}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`ua-badge ua-badge--${user.role || 'user'}`}>{user.role || 'user'}</span>
                      </td>
                      <td>
                        <div className="ua-session-cell">
                          <span className="ua-session-cell__time">{formatDuration(totalSecs)}</span>
                          <div className="ua-session-cell__bar-bg">
                            <div className="ua-session-cell__bar" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`ua-session-today ${todaySecs > 0 ? 'ua-session-today--active' : ''}`}>
                          {todaySecs > 0 ? formatDuration(todaySecs) : '—'}
                        </span>
                      </td>
                      <td>
                        <div className="ua-login-info">
                          <span className={`ua-online-dot ${online ? 'ua-online-dot--on' : ''}`} />
                          {timeAgo(user.lastLogin)}
                        </div>
                      </td>
                      <td>
                        {online ? (
                          <span className="ua-badge ua-badge--active" style={{ background: '#ecfdf5', color: '#059669' }}>
                            🟢 Online
                          </span>
                        ) : totalSecs > 0 ? (
                          <span className="ua-badge" style={{ background: '#f1f5f9', color: '#64748b' }}>
                            Tracked
                          </span>
                        ) : (
                          <span className="ua-empty-text">No data</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  /* ── Main render ── */
  if (loading) {
    return (
      <div className="ua-page">
        <div className="ua-header">
          <button className="ua-back-btn" onClick={() => navigate('/dashboard')}>← Back to Dashboard</button>
          <h1 className="ua-title">User Analytics</h1>
        </div>
        <div className="ua-loading">
          <div className="ua-loading__spinner" />
          <p>Loading analytics data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ua-page">
      <div className="ua-header">
        <button className="ua-back-btn" onClick={() => navigate('/dashboard')}>← Back to Dashboard</button>
        <div>
          <h1 className="ua-title">User Analytics</h1>
          <p className="ua-subtitle">
            Insights across {analytics.totalUsers} users · {analytics.onlineNow.length} online now · Region: {regionNameMap[selectedRegion] || selectedRegion}
          </p>
        </div>
        <button className="ua-refresh-btn" onClick={loadData}>🔄 Refresh</button>
      </div>

      <div className="ua-tabs">
        {[
          { key: 'overview', label: '📊 Overview' },
          { key: 'online', label: `🟢 Online (${analytics.onlineNow.length})` },
          { key: 'requests', label: '📋 Requests' },
          { key: 'sessions', label: '⏱️ Session Time' },
          { key: 'users', label: '👥 All Users' },
        ].map(tab => (
          <button
            key={tab.key}
            className={`ua-tab ${activeTab === tab.key ? 'ua-tab--active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="ua-content">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'online' && renderOnlineNow()}
        {activeTab === 'requests' && renderRequests()}
        {activeTab === 'sessions' && renderSessionTime()}
        {activeTab === 'users' && renderUserList()}
      </div>
    </div>
  );
}

export default UserAnalyticsPage;